import React, { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import ClarificationThread from './ClarificationThread';
import ClarificationDialogStepper from './ClarificationDialogStepper';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import { useFormDisclosure } from '@/features/submission_forms/ClarificationNavigationContext';

interface ClarificationIconProps {
  submissionId: string | undefined;
  questionSlug: string;
  /**
   * Pass `question.prompt` — the backend already delivers it display-ready
   * (no `{{...}}` markers), so it's safe to render as-is. Never pass
   * `question.prompt_template` here.
   */
  questionLabel?: string;
}

const ClarificationIcon: React.FC<ClarificationIconProps> = ({
  submissionId,
  questionSlug,
  questionLabel,
}) => {
  // Inside the submission form the open dialog is shared state, so the
  // clarification navigator can move from one field's dialog to the next.
  const disclosure = useFormDisclosure();
  const [localOpen, setLocalOpen] = useState(false);
  const isOpen = disclosure
    ? disclosure.openDialogSlug === questionSlug
    : localOpen;
  const setIsOpen = (open: boolean) => {
    if (!disclosure) {
      setLocalOpen(open);
    } else if (open) {
      disclosure.setOpenDialogSlug(questionSlug);
    } else if (disclosure.openDialogSlug === questionSlug) {
      disclosure.setOpenDialogSlug(null);
    }
  };
  // Another field's dialog took over (stepper Next/Previous): don't hand focus
  // back to this trigger, which would scroll the page back to it.
  const handedOffToAnotherDialog =
    !!disclosure &&
    disclosure.openDialogSlug !== null &&
    disclosure.openDialogSlug !== questionSlug;

  // Use shared context data (no individual API calls)
  const {
    getCountForQuestion,
    getUnresolvedCountForQuestion,
    canAddClarifications,
  } = useClarificationsContext();

  const isAdmin = canAddClarifications;

  const count = getCountForQuestion(questionSlug);
  const unresolvedCount = getUnresolvedCountForQuestion(questionSlug);

  // Don't render if no submissionId (new submission)
  if (!submissionId) {
    return null;
  }

  const allResolved = count > 0 && unresolvedCount === 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        data-clarification-trigger={questionSlug}
        title={isAdmin ? 'View clarifications' : 'View items needing attention'}
        className={`flex items-center justify-center w-7 h-7 rounded-[6px] border transition-colors ${
          allResolved
            ? 'bg-green-100 border-green-200'
            : unresolvedCount > 0
              ? 'bg-amber-100 border-amber-200'
              : 'bg-transparent border-gray-200'
        }`}
      >
        {allResolved ? (
          <MessageSquare className="w-[13px] h-[13px] text-green-600" />
        ) : unresolvedCount > 0 ? (
          <span className="text-[10px] font-semibold leading-none text-center text-amber-700 font-inter">
            {unresolvedCount}
          </span>
        ) : (
          <MessageSquare className="w-[13px] h-[13px] text-gray-300" />
        )}
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="sm:max-w-2xl max-h-[80vh] overflow-y-auto"
          onCloseAutoFocus={event => {
            if (handedOffToAnotherDialog) event.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {isAdmin ? 'Clarifications' : 'Items Needing Attention'}
            </DialogTitle>
            <p className="font-inter font-normal text-xs leading-[17px] text-gray-500 shrink-0 self-stretch">
              {questionLabel}
              {unresolvedCount > 0 ? (
                <span className="text-amber-500">
                  {' '}
                  · {unresolvedCount} open
                </span>
              ) : count > 0 ? (
                ` · ${count} total`
              ) : (
                ''
              )}
            </p>
          </DialogHeader>
          <Separator className="-mx-6 mt-2 mb-0" />
          <div className="mt-3">
            <ClarificationThread
              submissionId={submissionId}
              questionSlug={questionSlug}
            />
          </div>
          <ClarificationDialogStepper questionSlug={questionSlug} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClarificationIcon;
